/**
 * Fabric Cafe — kit reward cart status (theme asset)
 *
 * Upload to Shopify theme: Assets → kit-reward-status.js
 * Include in cart template:
 *   <script src="{{ 'kit-reward-status.js' | asset_url }}" defer></script>
 *
 * Global API: window.FabricCafeKitReward
 *
 * Optional per-line metadata from Liquid (recommended for reward collections):
 *   window.FabricCafeKitRewardLineMeta = {
 *     "{{ item.key }}": {
 *       isKit3Yard: true,
 *       isKit5Yard: false,
 *       isRewardPattern: true,
 *       isRewardBook: false
 *     }
 *   };
 */
(function (global) {
  "use strict";

  var KIT_3_YARD_BLOCK_SIZE = 3;
  var KIT_5_YARD_BLOCK_SIZE = 2;

  var COLLECTION_IDS = {
    FREE_PATTERNS_3_YARD: "gid://shopify/Collection/524072452379",
    FREE_PATTERNS_5_YARD: "gid://shopify/Collection/524073435419",
    FREE_BOOKS_3_YARD: "gid://shopify/Collection/524072616219",
    FREE_BOOKS_5_YARD: "gid://shopify/Collection/524073042203",
  };

  var REWARD_COLLECTION_GROUPS = {
    FREE_PATTERNS: [
      COLLECTION_IDS.FREE_PATTERNS_3_YARD,
      COLLECTION_IDS.FREE_PATTERNS_5_YARD,
    ],
    FREE_BOOKS: [
      COLLECTION_IDS.FREE_BOOKS_3_YARD,
      COLLECTION_IDS.FREE_BOOKS_5_YARD,
    ],
  };

  var KIT_TAGS = {
    KIT_3_YARD: "kit-3-yard",
    KIT_5_YARD: "kit-5-yard",
  };

  function normalizeCollectionId(id) {
    if (id == null) {
      return "";
    }

    var value = String(id).trim();
    if (!value) {
      return "";
    }

    if (value.indexOf("gid://shopify/Collection/") === 0) {
      return value;
    }

    return "gid://shopify/Collection/" + value;
  }

  function isInAnyCollection(inCollections, collectionIds) {
    if (!Array.isArray(inCollections) || inCollections.length === 0) {
      return false;
    }

    var normalizedTargets = collectionIds.map(normalizeCollectionId);

    return inCollections.some(function (entry) {
      if (!entry || !entry.isMember) {
        return false;
      }

      return normalizedTargets.indexOf(normalizeCollectionId(entry.collectionId)) !== -1;
    });
  }

  function getRewardCollectionFlags(product) {
    var inCollections = (product && product.inCollections) || [];

    return {
      isRewardPattern: isInAnyCollection(
        inCollections,
        REWARD_COLLECTION_GROUPS.FREE_PATTERNS,
      ),
      isRewardBook: isInAnyCollection(
        inCollections,
        REWARD_COLLECTION_GROUPS.FREE_BOOKS,
      ),
    };
  }

  function collectionMembershipFromFlags(flags) {
    var inCollections = [];

    if (flags.isRewardPattern) {
      REWARD_COLLECTION_GROUPS.FREE_PATTERNS.forEach(function (collectionId) {
        inCollections.push({ collectionId: collectionId, isMember: true });
      });
    }

    if (flags.isRewardBook) {
      REWARD_COLLECTION_GROUPS.FREE_BOOKS.forEach(function (collectionId) {
        inCollections.push({ collectionId: collectionId, isMember: true });
      });
    }

    return inCollections;
  }

  function getCartItemTags(item) {
    if (Array.isArray(item.product_tags)) {
      return item.product_tags;
    }

    if (Array.isArray(item.tags)) {
      return item.tags;
    }

    if (item.product && Array.isArray(item.product.tags)) {
      return item.product.tags;
    }

    return [];
  }

  function getLineMeta(item) {
    var metaMap = global.FabricCafeKitRewardLineMeta || {};

    if (item.key && metaMap[item.key]) {
      return metaMap[item.key];
    }

    if (item.variant_id != null && metaMap[String(item.variant_id)]) {
      return metaMap[String(item.variant_id)];
    }

    var variantPrefix = item.variant_id != null ? String(item.variant_id) + ":" : "";
    var keys = Object.keys(metaMap);

    for (var i = 0; i < keys.length; i++) {
      if (variantPrefix && keys[i].indexOf(variantPrefix) === 0) {
        return metaMap[keys[i]];
      }
    }

    return {};
  }

  function parseLineMetaFromSectionHtml(html) {
    if (!html) {
      return null;
    }

    var match = html.match(
      /<script[^>]*\bid=["']kit-reward-line-meta["'][^>]*>([\s\S]*?)<\/script>/i,
    );

    if (match && match[1]) {
      return JSON.parse(match[1].trim());
    }

    if (typeof DOMParser !== "undefined") {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var node = doc.getElementById("kit-reward-line-meta");

      if (node && node.textContent) {
        return JSON.parse(node.textContent.trim());
      }
    }

    return null;
  }

  function countKits(lines) {
    var kit3YardQty = 0;
    var kit5YardQty = 0;

    lines.forEach(function (line) {
      var merchandise = line.merchandise;
      if (!merchandise || merchandise.__typename !== "ProductVariant") {
        return;
      }

      var product = merchandise.product;
      if (!product) {
        return;
      }

      if (product.isKit3Yard) {
        kit3YardQty += line.quantity;
      }

      if (product.isKit5Yard) {
        kit5YardQty += line.quantity;
      }
    });

    return {
      kit3YardQty: kit3YardQty,
      kit5YardQty: kit5YardQty,
      totalKits: kit3YardQty + kit5YardQty,
    };
  }

  function calculateBookSlotLimits(counts) {
    var from3YardKits = Math.floor(counts.kit3YardQty / KIT_3_YARD_BLOCK_SIZE);
    var from5YardKits = Math.floor(counts.kit5YardQty / KIT_5_YARD_BLOCK_SIZE);

    return {
      from3YardKits: from3YardKits,
      from5YardKits: from5YardKits,
      total: from3YardKits + from5YardKits,
    };
  }

  function calculateMaxBooksRedeemable(kitCounts, bookQty) {
    var bookSlots = calculateBookSlotLimits(kitCounts);
    var maxBooks3 = Math.min(bookSlots.from3YardKits, bookQty);
    var maxBooks5 = Math.min(bookSlots.from5YardKits, bookQty);
    var maxBooks = 0;

    for (var books3 = 0; books3 <= maxBooks3; books3++) {
      for (var books5 = 0; books5 <= maxBooks5; books5++) {
        var books = books3 + books5;
        if (books > bookQty) {
          continue;
        }

        var kitCost =
          books3 * KIT_3_YARD_BLOCK_SIZE + books5 * KIT_5_YARD_BLOCK_SIZE;

        if (kitCost <= kitCounts.totalKits) {
          maxBooks = Math.max(maxBooks, books);
        }
      }
    }

    return maxBooks;
  }

  function calculateEarnedRewardLimits(kitCounts) {
    var bookSlots = calculateBookSlotLimits(kitCounts);

    return {
      maxPatterns: kitCounts.totalKits,
      maxBooks: bookSlots.total,
      bookSlots: bookSlots,
    };
  }

  function calculateRedemptionKitCost(kitCounts, plan) {
    var booksToDiscount = plan.booksToDiscount;
    var patternsToDiscount = plan.patternsToDiscount;

    if (booksToDiscount === 0) {
      return patternsToDiscount;
    }

    var maxBooks3 = Math.floor(kitCounts.kit3YardQty / KIT_3_YARD_BLOCK_SIZE);
    var maxBooks5 = Math.floor(kitCounts.kit5YardQty / KIT_5_YARD_BLOCK_SIZE);
    var minCost = Infinity;

    for (var books3 = 0; books3 <= maxBooks3; books3++) {
      for (var books5 = 0; books5 <= maxBooks5; books5++) {
        if (books3 + books5 !== booksToDiscount) {
          continue;
        }

        var cost =
          books3 * KIT_3_YARD_BLOCK_SIZE +
          books5 * KIT_5_YARD_BLOCK_SIZE +
          patternsToDiscount;

        if (cost <= kitCounts.totalKits) {
          minCost = Math.min(minCost, cost);
        }
      }
    }

    return minCost === Infinity ? 0 : minCost;
  }

  function calculateMixedRedemption(kitCounts, bookQty, patternQty) {
    var booksToDiscount = calculateMaxBooksRedeemable(kitCounts, bookQty);
    var bookKitCost = calculateRedemptionKitCost(kitCounts, {
      booksToDiscount: booksToDiscount,
      patternsToDiscount: 0,
    });
    var remainingKits = kitCounts.totalKits - bookKitCost;

    return {
      booksToDiscount: booksToDiscount,
      patternsToDiscount: Math.min(patternQty, Math.max(0, remainingKits)),
    };
  }

  function calculateRedemptionPlan(kitCounts, bookQty, patternQty) {
    var totalKits = kitCounts.totalKits;

    if (totalKits <= 0 || (bookQty === 0 && patternQty === 0)) {
      return { booksToDiscount: 0, patternsToDiscount: 0 };
    }

    if (bookQty === 0) {
      return {
        booksToDiscount: 0,
        patternsToDiscount: Math.min(patternQty, totalKits),
      };
    }

    if (patternQty === 0) {
      return {
        booksToDiscount: calculateMaxBooksRedeemable(kitCounts, bookQty),
        patternsToDiscount: 0,
      };
    }

    return calculateMixedRedemption(kitCounts, bookQty, patternQty);
  }

  function findRewardLines(lines, rewardType) {
    return lines.filter(function (line) {
      var merchandise = line.merchandise;
      if (!merchandise || merchandise.__typename !== "ProductVariant") {
        return false;
      }

      var flags = getRewardCollectionFlags(merchandise.product);
      return rewardType === "book" ? flags.isRewardBook : flags.isRewardPattern;
    });
  }

  function getRewardCartQuantities(lines) {
    var bookLines = findRewardLines(lines, "book");
    var patternLines = findRewardLines(lines, "pattern");

    return {
      bookLines: bookLines,
      patternLines: patternLines,
      bookQty: bookLines.reduce(function (sum, line) {
        return sum + line.quantity;
      }, 0),
      patternQty: patternLines.reduce(function (sum, line) {
        return sum + line.quantity;
      }, 0),
    };
  }

  function calculateRemainingRewardCapacity(
    kitCounts,
    earned,
    bookQty,
    patternQty,
    applied,
  ) {
    var booksCanAdd = 0;
    var bookHeadroom = Math.max(0, earned.maxBooks - applied.booksToDiscount);

    for (var bookExtra = 1; bookExtra <= bookHeadroom; bookExtra++) {
      var bookTestPlan = calculateRedemptionPlan(
        kitCounts,
        bookQty + bookExtra,
        patternQty,
      );

      if (bookTestPlan.booksToDiscount >= applied.booksToDiscount + bookExtra) {
        booksCanAdd = bookExtra;
        continue;
      }

      break;
    }

    var patternsCanAdd = 0;
    var patternHeadroom = Math.max(
      0,
      earned.maxPatterns - applied.patternsToDiscount,
    );
    var maxPatternProbe = patternQty + patternHeadroom + kitCounts.totalKits;

    for (var patternExtra = 1; patternExtra <= maxPatternProbe; patternExtra++) {
      var patternTestPlan = calculateRedemptionPlan(
        kitCounts,
        bookQty,
        patternQty + patternExtra,
      );

      if (
        patternTestPlan.patternsToDiscount >=
        applied.patternsToDiscount + patternExtra
      ) {
        patternsCanAdd = patternExtra;
        continue;
      }

      break;
    }

    return {
      booksCanAdd: booksCanAdd,
      patternsCanAdd: patternsCanAdd,
    };
  }

  function buildKitRewardStatus(lines) {
    var kitCounts = countKits(lines);
    var earned = calculateEarnedRewardLimits(kitCounts);
    var rewardCart = getRewardCartQuantities(lines);
    var applied = calculateRedemptionPlan(
      kitCounts,
      rewardCart.bookQty,
      rewardCart.patternQty,
    );
    var budgetUsed = calculateRedemptionKitCost(kitCounts, applied);
    var budgetRemaining = Math.max(0, kitCounts.totalKits - budgetUsed);
    var remaining = calculateRemainingRewardCapacity(
      kitCounts,
      earned,
      rewardCart.bookQty,
      rewardCart.patternQty,
      applied,
    );

    var patternsOnlyPlan = calculateRedemptionPlan(
      kitCounts,
      0,
      earned.maxPatterns,
    );
    var booksOnlyPlan = calculateRedemptionPlan(kitCounts, earned.maxBooks, 0);

    var hasQualifyingKits = kitCounts.totalKits > 0;
    var hasRewardsInCart =
      rewardCart.bookQty > 0 || rewardCart.patternQty > 0;
    var hasUnclaimedRewards =
      remaining.booksCanAdd > 0 ||
      remaining.patternsCanAdd > 0 ||
      (!hasRewardsInCart && (earned.maxBooks > 0 || earned.maxPatterns > 0));
    var isFullyRedeemed =
      hasQualifyingKits && hasRewardsInCart && !hasUnclaimedRewards;
    var shouldShowMessage =
      hasQualifyingKits &&
      (!hasRewardsInCart || hasUnclaimedRewards || isFullyRedeemed);

    return {
      kits: {
        kit3YardQty: kitCounts.kit3YardQty,
        kit5YardQty: kitCounts.kit5YardQty,
        totalKits: kitCounts.totalKits,
      },
      earned: {
        maxPatterns: earned.maxPatterns,
        maxBooks: earned.maxBooks,
        bookSlots: {
          from3YardKits: earned.bookSlots.from3YardKits,
          from5YardKits: earned.bookSlots.from5YardKits,
          total: earned.bookSlots.total,
        },
      },
      inCart: {
        bookQty: rewardCart.bookQty,
        patternQty: rewardCart.patternQty,
      },
      applied: {
        booksToDiscount: applied.booksToDiscount,
        patternsToDiscount: applied.patternsToDiscount,
      },
      budget: {
        total: kitCounts.totalKits,
        used: budgetUsed,
        remaining: budgetRemaining,
      },
      remaining: {
        booksCanAdd: remaining.booksCanAdd,
        patternsCanAdd: remaining.patternsCanAdd,
      },
      options: {
        patternsOnly: {
          patterns: patternsOnlyPlan.patternsToDiscount,
          books: 0,
        },
        booksOnly: {
          patterns: 0,
          books: booksOnlyPlan.booksToDiscount,
        },
        currentMixed: {
          patterns: applied.patternsToDiscount,
          books: applied.booksToDiscount,
        },
      },
      flags: {
        hasQualifyingKits: hasQualifyingKits,
        hasRewardsInCart: hasRewardsInCart,
        hasUnclaimedRewards: hasUnclaimedRewards,
        isFullyRedeemed: isFullyRedeemed,
        shouldShowMessage: shouldShowMessage,
      },
    };
  }

  function mapCartJsItemToLine(item, options) {
    var opts = options || {};
    var meta = getLineMeta(item);
    var tags = getCartItemTags(item);
    var inCollections =
      opts.inCollections ||
      (item.product && item.product.inCollections) ||
      [];

    if (
      inCollections.length === 0 &&
      (opts.isRewardPattern ||
        opts.isRewardBook ||
        meta.isRewardPattern ||
        meta.isRewardBook)
    ) {
      inCollections = collectionMembershipFromFlags({
        isRewardPattern: !!(opts.isRewardPattern || meta.isRewardPattern),
        isRewardBook: !!(opts.isRewardBook || meta.isRewardBook),
      });
    }

    if (Array.isArray(meta.collectionIds) && meta.collectionIds.length > 0) {
      meta.collectionIds.forEach(function (collectionId) {
        inCollections.push({
          collectionId: normalizeCollectionId(collectionId),
          isMember: true,
        });
      });
    }

    return {
      id: item.key,
      quantity: item.quantity,
      merchandise: {
        __typename: "ProductVariant",
        product: {
          isKit3Yard:
            opts.isKit3Yard != null
              ? opts.isKit3Yard
              : meta.isKit3Yard != null
                ? meta.isKit3Yard
                : tags.indexOf(KIT_TAGS.KIT_3_YARD) !== -1,
          isKit5Yard:
            opts.isKit5Yard != null
              ? opts.isKit5Yard
              : meta.isKit5Yard != null
                ? meta.isKit5Yard
                : tags.indexOf(KIT_TAGS.KIT_5_YARD) !== -1,
          inCollections: inCollections,
        },
      },
    };
  }

  function mapCartJsItemsToLines(items, mapItem) {
    var mapper = mapItem || mapCartJsItemToLine;

    return (items || [])
      .map(function (item) {
        return mapper(item);
      })
      .filter(function (line) {
        return line !== null;
      });
  }

  function buildKitRewardStatusFromCartJs(cart, mapItem) {
    var lines = mapCartJsItemsToLines(cart.items || [], mapItem);
    return buildKitRewardStatus(lines);
  }

  function attrIsTrue(element, attributeName) {
    var value = element.getAttribute(attributeName);
    return value === "true" || value === "1";
  }

  /**
   * Read per-line flags from cart row data attributes (optional theme markup).
   * data-kit-reward-key="{{ item.key }}"
   * data-kit-3-yard / data-kit-5-yard / data-reward-pattern / data-reward-book
   */
  function syncLineMetaFromDom(options) {
    if (typeof document === "undefined") {
      return global.FabricCafeKitRewardLineMeta || {};
    }

    var opts = options || {};
    var meta = Object.assign({}, global.FabricCafeKitRewardLineMeta || {});
    var selector =
      opts.rowSelector ||
      "[data-kit-reward-key], [data-cart-item-key], [data-line-key]";
    var rows = document.querySelectorAll(selector);

    rows.forEach(function (row) {
      var key =
        row.getAttribute("data-kit-reward-key") ||
        row.getAttribute("data-cart-item-key") ||
        row.getAttribute("data-line-key") ||
        row.getAttribute("data-key");

      if (!key) {
        return;
      }

      meta[key] = Object.assign({}, meta[key] || {}, {
        isKit3Yard: attrIsTrue(row, "data-kit-3-yard"),
        isKit5Yard: attrIsTrue(row, "data-kit-5-yard"),
        isRewardPattern: attrIsTrue(row, "data-reward-pattern"),
        isRewardBook: attrIsTrue(row, "data-reward-book"),
      });
    });

    global.FabricCafeKitRewardLineMeta = meta;
    return meta;
  }

  function shopifyRoot() {
    if (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) {
      return global.Shopify.routes.root;
    }

    return "/";
  }

  function buildSectionFetchUrls(sectionId) {
    var urls = [];
    var root = shopifyRoot().replace(/\/?$/, "/");

    if (global.FabricCafeKitRewardMetaUrl) {
      urls.push(global.FabricCafeKitRewardMetaUrl);
    }

    urls.push(root + "cart?sections=" + encodeURIComponent(sectionId));

    if (typeof window !== "undefined" && window.location) {
      var path = window.location.pathname || "";

      if (path.indexOf("/cart") !== -1) {
        urls.push(path + "?sections=" + encodeURIComponent(sectionId));
      }
    }

    return urls.filter(function (url, index, list) {
      return list.indexOf(url) === index;
    });
  }

  function extractSectionHtml(sections, sectionId) {
    if (!sections || typeof sections !== "object") {
      return null;
    }

    if (sections[sectionId]) {
      return sections[sectionId];
    }

    var keys = Object.keys(sections);

    for (var i = 0; i < keys.length; i++) {
      if (keys[i].indexOf(sectionId) !== -1) {
        return sections[keys[i]];
      }
    }

    if (keys.length === 1) {
      return sections[keys[0]];
    }

    return null;
  }

  function isCartJsonPayload(data) {
    return !!(
      data &&
      typeof data === "object" &&
      Array.isArray(data.items) &&
      data.token !== undefined
    );
  }

  function fetchSectionJson(url) {
    return fetch(url, {
      credentials: "same-origin",
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("Section fetch failed (" + response.status + "): " + url);
      }

      return response.json();
    }).then(function (data) {
      if (isCartJsonPayload(data)) {
        throw new Error(
          "Received cart.js instead of section HTML from " +
            url +
            ". Do not use Accept: application/json for section requests.",
        );
      }

      return data;
    });
  }

  /**
   * Rebuild line meta from a theme section via Section Rendering API.
   * Create sections/kit-reward-meta.liquid in the theme (see theme-assets/).
   */
  function refreshLineMetaFromSection(sectionId) {
    var id =
      sectionId ||
      global.FabricCafeKitRewardMetaSectionId ||
      "kit-reward-meta";
    var urls = buildSectionFetchUrls(id);

    function tryUrl(index) {
      if (index >= urls.length) {
        throw new Error(
          "Kit reward meta section missing from response for section id: " + id,
        );
      }

      return fetchSectionJson(urls[index]).then(function (sections) {
        var html = extractSectionHtml(sections, id);

        if (!html) {
          var keys =
            sections && typeof sections === "object"
              ? Object.keys(sections).join(", ")
              : "none";
          throw new Error(
            "Section '" +
              id +
              "' not in response from " +
              urls[index] +
              ". Keys: " +
              keys,
          );
        }

        var parsed = parseLineMetaFromSectionHtml(html);

        if (!parsed) {
          throw new Error(
            "Kit reward meta JSON not found in section HTML from " + urls[index],
          );
        }

        global.FabricCafeKitRewardLineMeta = parsed;
        return global.FabricCafeKitRewardLineMeta;
      }).catch(function (error) {
        if (index + 1 < urls.length) {
          return tryUrl(index + 1);
        }

        throw error;
      });
    }

    return tryUrl(0);
  }

  function notifyKitRewardCartUpdate() {
    setTimeout(function () {
      if (typeof global.__kitRewardUpdateCallback === "function") {
        global.__kitRewardUpdateCallback();
      }
    }, 200);
  }

  function hookCartFetchForKitReward() {
    if (global.__kitRewardFetchHooked || typeof window.fetch !== "function") {
      return;
    }

    global.__kitRewardFetchHooked = true;

    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var requestUrl = typeof url === "string" ? url : (url && url.url) || "";

      return originalFetch.apply(this, arguments).then(function (response) {
        if (/\/cart\/(add|change|update|clear)(\.js)?/.test(requestUrl)) {
          notifyKitRewardCartUpdate();
        }
        return response;
      });
    };
  }

  function hookCartXHRForKitReward() {
    if (global.__kitRewardXHRHooked || !window.XMLHttpRequest) {
      return;
    }

    global.__kitRewardXHRHooked = true;

    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__kitRewardRequestUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var requestUrl = String(xhr.__kitRewardRequestUrl || "");

      xhr.addEventListener("load", function () {
        if (/\/cart\/(add|change|update|clear)\.js/.test(requestUrl)) {
          notifyKitRewardCartUpdate();
        }
      });

      return originalSend.apply(this, arguments);
    };
  }

  function installCartUpdateHooks(callback) {
    global.__kitRewardUpdateCallback = callback;

    if (global.__kitRewardHooksInstalled) {
      return;
    }

    global.__kitRewardHooksInstalled = true;

    hookCartFetchForKitReward();
    hookCartXHRForKitReward();

    document.addEventListener("cart:updated", notifyKitRewardCartUpdate);
    document.addEventListener("cart:refresh", notifyKitRewardCartUpdate);
    document.addEventListener("theme:cart:change", notifyKitRewardCartUpdate);
    document.addEventListener("shopify:section:load", notifyKitRewardCartUpdate);
  }

  function prepareLineMeta(options) {
    var opts = options || {};

    if (opts.refreshMeta === false) {
      syncLineMetaFromDom(opts.domOptions);
      return Promise.resolve(global.FabricCafeKitRewardLineMeta);
    }

    return refreshLineMetaFromSection(opts.metaSectionId)
      .catch(function (error) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("Kit reward meta refresh failed; using cached meta.", error);
        }
        return global.FabricCafeKitRewardLineMeta;
      })
      .then(function () {
        syncLineMetaFromDom(opts.domOptions);
        return global.FabricCafeKitRewardLineMeta;
      });
  }

  function fetchCartRewardStatus(options) {
    return prepareLineMeta(options).then(function () {
      return fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("Failed to load cart.js");
          }

          return response.json();
        })
        .then(function (cart) {
          return buildKitRewardStatusFromCartJs(cart);
        });
    });
  }

  function shopifyCartRoot() {
    var root = shopifyRoot();
    return root.charAt(root.length - 1) === "/" ? root : root + "/";
  }

  var REWARD_EXCLUDE_TAGS = ["hide-kit-reward", "DoNotDiscount"];
  var MAX_COLLECTION_PAGES = 20; // safety cap: 20 x 250 = 5,000 products

  function parseTagsString(tagsField) {
    if (Array.isArray(tagsField)) {
      return tagsField;
    }
    if (typeof tagsField !== "string" || !tagsField) {
      return [];
    }
    return tagsField.split(",").map(function (t) {
      return t.trim();
    });
  }

  function isWholesaleImage(src) {
    return typeof src === "string" && src.toLowerCase().indexOf("-ws") !== -1;
  }

  function buildCdnThumbUrl(src) {
    if (!src) {
      return "";
    }
    return src + (src.indexOf("?") === -1 ? "?width=240" : "&width=240");
  }

  function mapRawProductToRewardEntry(rawProduct) {
    var tags = parseTagsString(rawProduct.tags);
    var isExcluded = REWARD_EXCLUDE_TAGS.some(function (t) {
      return tags.indexOf(t) !== -1;
    });

    var image = rawProduct.images && rawProduct.images[0] ? rawProduct.images[0].src : "";

    if (isExcluded || isWholesaleImage(image)) {
      return null;
    }

    var variants = (rawProduct.variants || [])
      .filter(function (v) {
        return v.available;
      })
      .map(function (v) {
        return {
          variantId: v.id,
          variantTitle: v.title,
          available: v.available,
        };
      });

    return {
      productId: rawProduct.id,
      title: rawProduct.title,
      image: buildCdnThumbUrl(image),
      collectionTitle: "",
      available: variants.length > 0,
      createdAt: Math.floor(Date.parse(rawProduct.published_at || rawProduct.created_at) / 1000) || 0,
      variants: variants,
    };
  }

  function fetchCollectionProductsPage(handle, page) {
    var root = shopifyRoot().replace(/\/?$/, "/");
    var url = root + "collections/" + encodeURIComponent(handle) + "/products.json?limit=250&page=" + page;

    return fetch(url, { credentials: "same-origin" }).then(function (response) {
      if (!response.ok) {
        throw new Error("Collection fetch failed (" + response.status + "): " + url);
      }
      return response.json();
    }).then(function (data) {
      return (data && data.products) || [];
    });
  }

  function fetchAllCollectionProducts(handle) {
    if (!handle) {
      return Promise.resolve([]);
    }

    var all = [];

    function nextPage(page) {
      return fetchCollectionProductsPage(handle, page).then(function (products) {
        all = all.concat(products);

        if (products.length === 250 && page < MAX_COLLECTION_PAGES) {
          return nextPage(page + 1);
        }

        return all;
      });
    }

    return nextPage(1).catch(function (error) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn("Kit reward collection fetch failed for '" + handle + "'.", error);
      }
      return all;
    });
  }

  function buildRewardCatalogEntries(rawProducts) {
    return rawProducts
      .map(mapRawProductToRewardEntry)
      .filter(function (entry) {
        return entry !== null;
      });
  }

  function loadRewardProducts() {
    if (global.FabricCafeKitRewardProductsPromise) {
      return global.FabricCafeKitRewardProductsPromise;
    }

    var handles = global.FabricCafeKitRewardCollectionHandles || {};

    global.FabricCafeKitRewardProductsPromise = Promise.all([
      fetchAllCollectionProducts(handles.patterns),
      fetchAllCollectionProducts(handles.books),
    ]).then(function (results) {
      var catalog = {
        patterns: buildRewardCatalogEntries(results[0]),
        books: buildRewardCatalogEntries(results[1]),
      };
      global.FabricCafeKitRewardProducts = catalog;
      return catalog;
    }).catch(function (error) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn("Kit reward product catalog load failed.", error);
      }
      var fallback = { patterns: [], books: [] };
      global.FabricCafeKitRewardProducts = fallback;
      return fallback;
    });

    return global.FabricCafeKitRewardProductsPromise;
  }


  function uniqueRewardProducts(products) {
    var seen = {};
    var result = [];

    (products || []).forEach(function (product) {
      var id = String(product.productId);

      if (!id || seen[id]) {
        return;
      }

      seen[id] = true;
      result.push(product);
    });

    return result;
  }

  function getRewardPickerLimits(status) {
    var earned = status.earned;
    var remaining = status.remaining || {};

    if (!status.flags.hasRewardsInCart) {
      return {
        patterns: earned.maxPatterns,
        books: earned.maxBooks,
      };
    }

    return {
      patterns: remaining.patternsCanAdd,
      books: remaining.booksCanAdd,
    };
  }

  function buildRewardSummaryText(status) {
    if (!status || !status.flags) {
      return "";
    }

    if (!status.flags.shouldShowMessage) {
      return "";
    }

    var earned = status.earned;
    var applied = status.applied;
    var budget = status.budget;

    if (!status.flags.hasRewardsInCart) {
      return (
        "Your " +
        budget.total +
        " kit(s) qualify for up to " +
        earned.maxPatterns +
        " free pattern" +
        (earned.maxPatterns === 1 ? "" : "s") +
        " or " +
        earned.maxBooks +
        " free book" +
        (earned.maxBooks === 1 ? "" : "s") +
        "."
      );
    }

    var appliedText = [];

    if (applied.patternsToDiscount > 0) {
      appliedText.push(
        applied.patternsToDiscount +
          " free pattern" +
          (applied.patternsToDiscount === 1 ? "" : "s"),
      );
    }

    if (applied.booksToDiscount > 0) {
      appliedText.push(
        applied.booksToDiscount +
          " free book" +
          (applied.booksToDiscount === 1 ? "" : "s"),
      );
    }

    if (status.flags.isFullyRedeemed) {
      return (
        "Your kit rewards are fully applied (" + appliedText.join(" and ") + ")."
      );
    }

    if (budget.remaining <= 0) {
      return (
        "Applied: " +
        appliedText.join(" and ") +
        ". Reward criteria met."
      );
    }

    var limits = getRewardPickerLimits(status);

    return (
      "Applied: " +
      appliedText.join(" and ") +
      ". " +
      budget.remaining +
      " kit reward credit" +
      (budget.remaining === 1 ? "" : "s") +
      " left (of " +
      budget.total +
      "). " +
      "That's up to " +
      limits.patterns +
      " more pattern" +
      (limits.patterns === 1 ? "" : "s") +
      ", or " +
      limits.books +
      " more book" +
      (limits.books === 1 ? "" : "s") +
      "."
    );
  }

function ensurePickerStyles() {
    if (document.getElementById("kit-reward-picker-styles")) {
      return;
    }

    var style = document.createElement("style");
    style.id = "kit-reward-picker-styles";
    style.textContent =
      "#kit-reward-message{text-align:center}" +
      ".kit-reward-message__text{text-align:center;margin:0 auto}" +
      ".kit-reward-message__actions{margin-top:.75rem;display:flex;flex-wrap:wrap;gap:.5rem;justify-content:center}" +
      ".kit-reward-message__btn,.kit-reward-picker__add{" +
      "appearance:none;border:1px solid currentColor;background:transparent;" +
      "padding:.45rem .85rem;border-radius:4px;cursor:pointer;font:inherit}" +
      ".kit-reward-message__btn:hover,.kit-reward-picker__add:hover{opacity:.85}" +
      ".kit-reward-picker{position:fixed;inset:0;z-index:9999;display:flex;" +
      "align-items:center;justify-content:center;padding:1rem}" +
      ".kit-reward-picker[hidden]{display:none}" +
      ".kit-reward-picker__overlay{position:absolute;inset:0;background:rgba(0,0,0,.45)}" +
      ".kit-reward-picker__dialog{position:relative;background:#fff;color:#111;" +
      "max-width:720px;width:100%;max-height:85vh;overflow:auto;border-radius:8px;" +
      "padding:1rem 1rem 1.25rem;box-shadow:0 12px 40px rgba(0,0,0,.2)}" +
      ".kit-reward-picker__header{display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-bottom:.75rem}" +
      ".kit-reward-picker__close{appearance:none;border:0;background:transparent;" +
      "font-size:2.5rem;line-height:1;cursor:pointer}" +
      ".kit-reward-picker__controls{display:flex;gap:.5rem;margin-bottom:1rem;flex-wrap:wrap}" +
      ".kit-reward-picker__search{flex:1 1 200px;padding:.5rem .65rem;border:1px solid #ccc;" +
      "border-radius:4px;font:inherit}" +
      ".kit-reward-picker__sort{flex:0 0 auto;padding:.5rem .65rem;border:1px solid #ccc;" +
      "border-radius:4px;font:inherit;background:#fff}" +
      ".kit-reward-picker__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:.75rem}" +
      ".kit-reward-picker__card{border:1px solid #ddd;border-radius:6px;padding:.75rem;text-align:center}" +
      ".kit-reward-picker__image{width:100%;aspect-ratio:1/1;object-fit:cover;object-position:top;" +
      "border-radius:4px;background:#f5f5f5}" +
      ".kit-reward-picker__title{margin:.6rem 0;font-size:1.50rem;line-height:1.35;font-weight:600}" +
      ".kit-reward-picker__variants{display:flex;gap:.4rem;justify-content:center;flex-wrap:wrap}" +
      ".kit-reward-picker__empty{padding:1rem 0;text-align:center;opacity:.75}";

    document.head.appendChild(style);
  }

  function getPickerModal() {
      var modal = document.getElementById("kit-reward-picker");

      if (modal) {
        return modal;
      }

      ensurePickerStyles();

      modal = document.createElement("div");
      modal.id = "kit-reward-picker";
      modal.className = "kit-reward-picker";
      modal.hidden = true;
      modal.innerHTML =
        '<div class="kit-reward-picker__overlay" data-kit-reward-close></div>' +
        '<div class="kit-reward-picker__dialog" role="dialog" aria-modal="true" aria-labelledby="kit-reward-picker-title">' +
        '<div class="kit-reward-picker__header">' +
        '<h3 id="kit-reward-picker-title"></h3>' +
        '<button type="button" class="kit-reward-picker__close" data-kit-reward-close aria-label="Close">&times;</button>' +
        "</div>" +
        '<div class="kit-reward-picker__controls">' +
        '<input type="search" class="kit-reward-picker__search" id="kit-reward-picker-search" placeholder="Search by name…" aria-label="Search products">' +
        '<select class="kit-reward-picker__sort" id="kit-reward-picker-sort" aria-label="Sort products">' +
        '<option value="newest">Newest to oldest</option>' +
        '<option value="az">A - Z</option>' +
        "</select>" +
        "</div>" +
        '<div class="kit-reward-picker__grid" id="kit-reward-picker-grid"></div>' +
        "</div>";

      modal.addEventListener("click", function (event) {
        if (event.target.closest("[data-kit-reward-close]")) {
          closeRewardPicker();
        }
      });

      var searchInput = modal.querySelector("#kit-reward-picker-search");
      var sortSelect = modal.querySelector("#kit-reward-picker-sort");

      searchInput.addEventListener("input", function () {
        renderPickerGrid();
      });

      sortSelect.addEventListener("change", function () {
        renderPickerGrid();
      });

      document.body.appendChild(modal);
      return modal;
    }

  function closeRewardPicker() {
    var modal = document.getElementById("kit-reward-picker");

    if (modal) {
      modal.hidden = true;
    }
  }

  function addRewardVariantToCart(variantId) {
    return fetch(shopifyCartRoot() + "cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || "Add to cart failed");
        }

        return data;
      });
    });
  }

  function buildCartSectionsUrl(sectionIds) {
    var root = shopifyRoot().replace(/\/?$/, "/");
    var path =
      typeof window !== "undefined" &&
      window.location &&
      window.location.pathname.indexOf("/cart") !== -1
        ? window.location.pathname
        : root + "cart";

    return (
      path +
      "?sections=" +
      sectionIds.map(function (sectionId) {
        return encodeURIComponent(sectionId);
      }).join(",")
    );
  }

  function getCartSectionIdsToRefresh() {
    if (
      Array.isArray(global.FabricCafeKitRewardCartSectionIds) &&
      global.FabricCafeKitRewardCartSectionIds.length > 0
    ) {
      return global.FabricCafeKitRewardCartSectionIds.slice();
    }

    if (typeof document === "undefined") {
      return [];
    }

    var ids = [];

    document.querySelectorAll('[id^="shopify-section-"]').forEach(function (element) {
      var sectionId = element.id.replace(/^shopify-section-/, "");

      if (!sectionId || sectionId === "kit-reward-meta") {
        return;
      }

      if (/custom_liquid/i.test(sectionId)) {
        return;
      }

      if (/footer|toolbar|halo-/i.test(sectionId)) {
        return;
      }

      if (
        element.querySelector(
          "#kit-reward-products, #kit-reward-line-meta, script[src*='kit-reward-status']",
        )
      ) {
        return;
      }

      ids.push(sectionId);
    });

    return ids;
  }

  function replaceSectionHtml(sectionId, html) {
    var current = document.getElementById("shopify-section-" + sectionId);

    if (!current || !html) {
      return false;
    }

    var template = document.createElement("div");
    template.innerHTML = html;
    var replacement =
      template.querySelector("#shopify-section-" + sectionId) ||
      template.firstElementChild;

    if (!replacement || !current.parentNode) {
      return false;
    }

    var imported = document.importNode(replacement, true);
    current.parentNode.replaceChild(imported, current);

    document.dispatchEvent(
      new CustomEvent("shopify:section:load", {
        bubbles: true,
        detail: { sectionId: sectionId },
      }),
    );

    return true;
  }

  function refreshCartPageSections() {
    if (typeof document === "undefined") {
      return Promise.resolve(false);
    }

    var sectionIds = getCartSectionIdsToRefresh();

    if (sectionIds.length === 0) {
      return Promise.resolve(false);
    }

    return fetchSectionJson(buildCartSectionsUrl(sectionIds))
      .then(function (sections) {
        var refreshed = false;

        sectionIds.forEach(function (sectionId) {
          if (replaceSectionHtml(sectionId, sections[sectionId])) {
            refreshed = true;
          }
        });

        return refreshed;
      })
      .catch(function (error) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("Kit reward cart section refresh failed.", error);
        }

        return false;
      });
  }

  function triggerStorefrontCartRefresh() {
    notifyKitRewardCartUpdate();
    document.dispatchEvent(new CustomEvent("cart:updated"));
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  function refreshStorefrontAfterCartChange() {
    return refreshCartPageSections().then(function () {
      triggerStorefrontCartRefresh();
    });
  }

 var currentPickerState = {
    type: null,
    status: null,
    products: [],
  };

  function buildRewardCard(product) {
    var card = document.createElement("article");
    card.className = "kit-reward-picker__card";

    var imageHtml = product.image
      ? '<img class="kit-reward-picker__image" src="' +
        product.image +
        '" alt="" loading="lazy">'
      : '<div class="kit-reward-picker__image"></div>';

    var variants = (product.variants || []).filter(function (v) {
      return v.available;
    });

    card.innerHTML =
      imageHtml +
      '<p class="kit-reward-picker__title"></p>' +
      '<div class="kit-reward-picker__variants"></div>';

    card.querySelector(".kit-reward-picker__title").textContent = product.title;

    var variantWrap = card.querySelector(".kit-reward-picker__variants");

    if (variants.length === 0) {
      var disabledButton = document.createElement("button");
      disabledButton.type = "button";
      disabledButton.className = "kit-reward-picker__add";
      disabledButton.disabled = true;
      disabledButton.textContent = "Unavailable";
      variantWrap.appendChild(disabledButton);
    } else {
      variants.forEach(function (variant) {
        var button = document.createElement("button");
        button.type = "button";
        button.className = "kit-reward-picker__add";

        button.textContent =
          variants.length > 1
            ? (variant.variantTitle && variant.variantTitle !== "Default Title"
                ? variant.variantTitle
                : "Add to cart")
            : "Add to cart";

        button.addEventListener("click", function () {
          button.disabled = true;
          var original = button.textContent;
          button.textContent = "Adding…";

          addRewardVariantToCart(variant.variantId)
            .then(function () {
              closeRewardPicker();
              return refreshStorefrontAfterCartChange();
            })
            .catch(function (error) {
              button.disabled = false;
              button.textContent = original;
              window.alert(error.message || "Could not add this item to your cart.");
            });
        });

        variantWrap.appendChild(button);
      });
    }

    return card;
  }

  function renderPickerGrid() {
    var grid = document.getElementById("kit-reward-picker-grid");
    var searchInput = document.getElementById("kit-reward-picker-search");
    var sortSelect = document.getElementById("kit-reward-picker-sort");

    if (!grid) {
      return;
    }

    var query = (searchInput && searchInput.value ? searchInput.value : "")
      .trim()
      .toLowerCase();
    var sortMode = sortSelect ? sortSelect.value : "newest";

    var products = currentPickerState.products.filter(function (product) {
      return !query || product.title.toLowerCase().indexOf(query) !== -1;
    });

    products = products.slice().sort(function (a, b) {
      if (sortMode === "az") {
        return a.title.localeCompare(b.title);
      }
      // newest to oldest
      return (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0);
    });

    grid.innerHTML = "";

    if (products.length === 0) {
      grid.innerHTML =
        '<p class="kit-reward-picker__empty">No matching products found.</p>';
      return;
    }

    products.forEach(function (product) {
      grid.appendChild(buildRewardCard(product));
    });
  }

    function openRewardPicker(type, status) {
    var limits = getRewardPickerLimits(status);
    var limit = type === "book" ? limits.books : limits.patterns;

    if (limit <= 0) {
      return;
    }

    loadRewardProducts().then(function (catalog) {
      var products = uniqueRewardProducts(
        type === "book" ? catalog.books : catalog.patterns,
      ).filter(function (product) {
        return product.available;
      });

      var modal = getPickerModal();
      var title = document.getElementById("kit-reward-picker-title");
      var searchInput = document.getElementById("kit-reward-picker-search");

      if (!title) {
        return;
      }

      title.textContent =
        type === "book"
          ? "Choose a free book"
          : "Choose a free pattern";

      if (searchInput) {
        searchInput.value = "";
      }

      currentPickerState = {
        type: type,
        status: status,
        products: products,
      };

      renderPickerGrid();

      modal.hidden = false;
    });
  }


    function renderRewardMessage(container, status) {
    if (!container) {
      return;
    }

    ensurePickerStyles();

    container.innerHTML = "";

    var summary = buildRewardSummaryText(status);

    if (!summary) {
      return;
    }

    var text = document.createElement("p");
    text.className = "kit-reward-message__text";
    text.textContent = summary;
    container.appendChild(text);

    if (
      !status.flags ||
      status.flags.isFullyRedeemed ||
      (status.budget && status.budget.remaining <= 0)
    ) {
      return;
    }

    var limits = getRewardPickerLimits(status);

    loadRewardProducts().then(function (catalog) {
      var actions = document.createElement("div");
      actions.className = "kit-reward-message__actions";
      var hasActions = false;

      if (
        limits.patterns > 0 &&
        uniqueRewardProducts(catalog.patterns).some(function (product) {
          return product.available;
        })
      ) {
        var patternButton = document.createElement("button");
        patternButton.type = "button";
        patternButton.className = "kit-reward-message__btn";
        patternButton.textContent =
          "Choose a free pattern" +
          (limits.patterns > 1 ? " (" + limits.patterns + " available)" : "");
        patternButton.addEventListener("click", function () {
          openRewardPicker("pattern", status);
        });
        actions.appendChild(patternButton);
        hasActions = true;
      }

      if (
        limits.books > 0 &&
        uniqueRewardProducts(catalog.books).some(function (product) {
          return product.available;
        })
      ) {
        var bookButton = document.createElement("button");
        bookButton.type = "button";
        bookButton.className = "kit-reward-message__btn";
        bookButton.textContent =
          "Choose a free book" +
          (limits.books > 1 ? " (" + limits.books + " available)" : "");
        bookButton.addEventListener("click", function () {
          openRewardPicker("book", status);
        });
        actions.appendChild(bookButton);
        hasActions = true;
      }

      if (hasActions) {
        container.appendChild(actions);
      }
    });
  }


  global.FabricCafeKitReward = {
    COLLECTION_IDS: COLLECTION_IDS,
    KIT_TAGS: KIT_TAGS,
    buildKitRewardStatus: buildKitRewardStatus,
    buildKitRewardStatusFromCartJs: buildKitRewardStatusFromCartJs,
    mapCartJsItemToLine: mapCartJsItemToLine,
    mapCartJsItemsToLines: mapCartJsItemsToLines,
    syncLineMetaFromDom: syncLineMetaFromDom,
    refreshLineMetaFromSection: refreshLineMetaFromSection,
    installCartUpdateHooks: installCartUpdateHooks,
    fetchCartRewardStatus: fetchCartRewardStatus,
    loadRewardProducts: loadRewardProducts,
    buildRewardSummaryText: buildRewardSummaryText,
    renderRewardMessage: renderRewardMessage,
    openRewardPicker: openRewardPicker,
    closeRewardPicker: closeRewardPicker,
    refreshCartPageSections: refreshCartPageSections,
    refreshStorefrontAfterCartChange: refreshStorefrontAfterCartChange,
  };
})(window);